import { useEffect, useMemo, useState } from 'react'
import { Ban, ShieldAlert, ToggleRight, Zap } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { SecCrudTable, type SecField } from './SecCrudTable'
import { secListOnce, SEC_STR as S, type DdosRuleRow } from './securityMoreApi'

/**
 * A18.17 — DDoS-holati. Admin-managed rate/threshold rule config
 * (`sec_ddos_rules`, 000248): each rule pairs a threshold with a mitigation
 * action + scope + on/off. The status panel aggregates the live ruleset. Real
 * CRUD via `/admin/security/ddos-rules` — functional with no edge tool wired.
 */
const ACTION_OPTS = [
  { value: 'monitor', label: 'monitor' },
  { value: 'throttle', label: 'throttle' },
  { value: 'challenge', label: 'challenge' },
  { value: 'block', label: 'block' },
]

/** Live ruleset summary — total / active / blocking / max threshold. */
function DdosStatusPanel({ reloadKey }: { reloadKey: number }) {
  const [rows, setRows] = useState<DdosRuleRow[]>([])
  useEffect(() => {
    const ctrl = new AbortController()
    secListOnce<DdosRuleRow>('/admin/security/ddos-rules', 'rules', ctrl.signal)
      .then(setRows)
      .catch(() => setRows([]))
    return () => ctrl.abort()
  }, [reloadKey])

  const total = rows.length
  const active = rows.filter((r) => r.enabled).length
  const blocking = rows.filter((r) => r.enabled && r.action === 'block').length
  const maxThreshold = rows.reduce((m, r) => Math.max(m, Number(r.threshold) || 0), 0)

  const cards = [
    { label: S.panelTotal, value: String(total), icon: <ShieldAlert size={16} /> },
    { label: S.panelActive, value: String(active), icon: <ToggleRight size={16} /> },
    { label: S.panelBlocking, value: String(blocking), icon: <Ban size={16} /> },
    { label: S.panelMaxThreshold, value: maxThreshold.toLocaleString(), icon: <Zap size={16} /> },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 p-4 pb-0 sm:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="flex items-center gap-3 rounded-lg border border-border-subtle bg-surface-1 px-3 py-2.5"
        >
          <div className="text-accent">{c.icon}</div>
          <div className="min-w-0">
            <div className="text-lg font-semibold text-primary">{c.value}</div>
            <div className="truncate text-xs text-secondary">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

export function DdosScreen() {
  const columns = useMemo<AdminColumn<DdosRuleRow>[]>(
    () => [
      { key: 'rule', header: S.rule, type: 'text' },
      { key: 'threshold', header: S.threshold, type: 'number', width: 120 },
      { key: 'scope', header: S.scope, type: 'text', width: 160 },
      {
        key: 'action',
        header: S.action,
        type: 'enum',
        width: 140,
        options: [
          { value: 'block', label: 'block', status: 'blocked' },
          { value: 'challenge', label: 'challenge', status: 'review' },
          { value: 'throttle', label: 'throttle', status: 'pending' },
          { value: 'monitor', label: 'monitor', status: 'active' },
        ],
      },
      { key: 'enabled', header: S.enabledLbl, type: 'bool', width: 90, align: 'center' },
      { key: 'updated_at', header: S.updated, type: 'date', width: 150 },
    ],
    [],
  )

  const fields = useMemo<SecField[]>(
    () => [
      { name: 'rule', placeholder: S.rule },
      { name: 'threshold', placeholder: S.threshold, type: 'number' },
      { name: 'scope', placeholder: S.scope, mono: true },
      { name: 'action', placeholder: S.action, type: 'select', options: ACTION_OPTS },
      { name: 'enabled', placeholder: S.enabledLbl, type: 'checkbox', defaultValue: 'true' },
    ],
    [],
  )

  return (
    <SecCrudTable<DdosRuleRow>
      screenId="A18.17"
      title={S.ddos}
      addLabel={S.addDdos}
      path="/admin/security/ddos-rules"
      listKey="rules"
      searchKeys={['rule', 'scope', 'action']}
      fields={fields}
      columns={columns}
      icon={<Zap size={28} strokeWidth={1.5} />}
      topPanel={(reloadKey) => <DdosStatusPanel reloadKey={reloadKey} />}
    />
  )
}
